import { useContext, useEffect, useState } from "react"

import { Index, ProviderConnector, ProviderConnectorCollection } from "wealthmode-frontend/backend"
import {
  Box,
  Button,
  ButtonText,
  HStack,
  Input,
  InputField,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Text,
  VStack,
} from "wealthmode-frontend/gluestack/ui"

import { BackendContext } from "@/context"

/** Only provider connectors nothing has claimed can be linked. */
const UNLINKED = "eq(unlinked,true)"

/**
 * LinkCandidateModal searches the provider connectors nothing has claimed yet.
 * Only orphans are offered: a provider connector belongs to exactly one
 * connector, and moving one between connectors is deliberately not a thing -
 * unlink it from the old connector first.
 */
export function LinkCandidateModal({
  isOpen,
  onClose,
  onLink,
}: {
  isOpen: boolean
  onClose: () => void
  onLink: (providerConnectorID: string) => Promise<void>
}) {
  const backend = useContext(BackendContext)

  const [query, setQuery] = useState("")
  const [results, setResults] = useState<ProviderConnector[]>([])
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setQuery("")
      setResults([])
      setError(null)
    }
  }, [isOpen])

  async function search() {
    setSearching(true)
    setError(null)

    try {
      const data = backend.link("data").resolve() as Index
      await data.fetch({ reload: true })

      const collection = data.link("provider_connectors")?.resolve() as ProviderConnectorCollection | undefined
      if (!collection) {
        setError("Provider connectors resource is not available")
        return
      }

      await collection.fetch({
        query: new URLSearchParams({ q: query, where: UNLINKED, limit: "25" }),
        reload: true,
      })

      setResults(collection.models)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSearching(false)
    }
  }

  async function link(providerConnectorID: string) {
    setError(null)

    try {
      await onLink(providerConnectorID)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
    >
      <ModalBackdrop />
      <ModalContent className="bg-background-secondary border border-border-medium max-w-2xl w-full">
        <ModalHeader>
          <Text className="text-lg font-semibold text-foreground-primary">Link a provider connector</Text>
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <VStack className="gap-4">
            <HStack className="gap-2">
              <Input
                variant="outline"
                size="md"
                className="flex-1"
              >
                <InputField
                  value={query}
                  onChangeText={setQuery}
                  placeholder="Search unlinked provider connectors"
                  onSubmitEditing={() => void search()}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </Input>
              <Button
                variant="outline"
                onPress={() => void search()}
                isDisabled={searching}
              >
                <ButtonText>Search</ButtonText>
              </Button>
            </HStack>

            {searching ? <Spinner size="small" /> : null}

            <VStack className="gap-2">
              {results.map((result) => (
                <HStack
                  key={result.id}
                  className="items-center justify-between rounded-md border border-border-medium px-3 py-2"
                >
                  <VStack>
                    <Text className="text-sm text-foreground-primary">{result.attributes.name}</Text>
                    <Text className="text-xs text-foreground-secondary">
                      {result.attributes.provider} · {result.attributes.url ?? "no url"}
                    </Text>
                  </VStack>
                  <Button
                    size="sm"
                    variant="outline"
                    onPress={() => void link(result.id)}
                  >
                    <ButtonText>Link</ButtonText>
                  </Button>
                </HStack>
              ))}
              {!searching && results.length === 0 ? (
                <Text className="text-sm text-foreground-secondary">No unlinked provider connectors found.</Text>
              ) : null}
            </VStack>

            {error ? (
              <Box className="rounded-md border border-border-medium bg-background-tertiary px-3 py-2">
                <Text className="text-sm text-foreground-secondary whitespace-pre-wrap">{error}</Text>
              </Box>
            ) : null}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            onPress={onClose}
          >
            <ButtonText>Close</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
